import { legalLinks, siteLinks, socialLinks } from "@ms/us/src/@ms/gatsby-site/config/footer";
import { countries } from "@ms/us/src/@ms/gatsby-site/config/navigation";

import {
  AmexLogoWhite,
  DinersLogoWhite,
  DiscoverLogoWhite,
  FooterDataProps,
  JcbLogoWhite,
  MasterCardLogoWhite,
  PaypalLogoWhiteIcon,
  ReturnsIcon,
  SecureIcon,
  ShippingIcon,
  SiteLinkInterface,
  VisaLogoWhite,
} from "@ms/gatsby-theme";

import { manscapedTitle } from "@ms/gatsby-site/src/config/legal";

/**
 * Get Site Links For Blog
 *
 * For Blog site, we must force all links to be "external" because
 * the blog is not a subdomain of the main site and we do not want
 * to use the gatsby-link component
 *
 * @param links
 * @returns links
 */
const getSiteLinksForBlog = <T extends SiteLinkInterface>(links: T[]): T[] => {
  const baseUrl = "https://www.manscaped.com";

  return links.map((link) => {
    if (link.href) {
      return { ...link, href: link.href };
    }
    if (link.to) {
      return { ...link, href: `${baseUrl}${link.to}`, to: undefined };
    } else {
      return link;
    }
  });
};

/**
 * Use Blog Footer Data
 *
 * this takes in the footer config for the given instance and creates the footer Data to allow
 * all footer component to live in our gatsby-theme component library so we can use
 * the same footer in multiple places
 *
 */
export const useBlogFooterData = (): FooterDataProps => {
  // hardcoding for blog because US config requires several imports that are not available in blog
  // made note in US billing.tsx config if it ever gets updates
  const availablePaymentMethods = [
    VisaLogoWhite,
    MasterCardLogoWhite,
    AmexLogoWhite,
    DiscoverLogoWhite,
    JcbLogoWhite,
    DinersLogoWhite,
    PaypalLogoWhiteIcon,
  ];
  const valuePropositions = [
    {
      title: "free shipping",
      height: [25, null, 46],
      Icon: ShippingIcon,
    },
    {
      title: "hassle-free returns",
      height: [31, null, 52],
      Icon: ReturnsIcon,
    },
    {
      title: "Secure checkout",
      height: [30, null, 45],
      Icon: SecureIcon,
    },
  ];

  const headers = {
    footerPrivacyPreferences: "Privacy Preferences",
    footerAcceptedPaymentMethods: "Accepted Payment Methods",
    footerEmailTitle: "Get Early Access to New Products Below",
    footerAllRightsReserved: "All Rights Reserved",
  };

  return {
    manscapedTitle: manscapedTitle,
    currentMsMarket: "blog",
    headers: headers,
    valuePropositions: valuePropositions,
    availablePaymentMethods: availablePaymentMethods,
    siteLinks: getSiteLinksForBlog(siteLinks),
    socialLinks: getSiteLinksForBlog(socialLinks),
    legalLinks: getSiteLinksForBlog(legalLinks),
    countries: countries,
  };
};
